import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, X, Pencil, Trash2, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const STATUSES   = ['open', 'in_progress', 'resolved', 'closed'];

const PRI_COLOR = {
  low:    'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-700',
  high:   'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700',
};
const STA_COLOR = {
  open:        'bg-rose-50 text-rose-700 border-rose-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed:      'bg-slate-100 text-slate-600 border-slate-200',
};

const blank = () => ({
  title: '', category: '', priority: 'medium', location: '',
  description: '', reported_by: '', assigned_to: '', target_date: '',
});

export default function AdminMaintenance() {
  const [items, setItems] = useState([]);
  const [lookup, setLookup] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => {
    const q = statusFilter ? `?status=${statusFilter}` : '';
    apiGet(`/admin/maintenance${q}`).then(r => setItems(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { apiGet('/admin/lookup').then(setLookup).catch(() => {}); }, []);

  const openNew  = ()  => { setEditing(null); setForm(blank()); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, target_date: r.target_date || '' }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category) { toast.error('Title and Category are required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiPut(`/admin/maintenance/${editing.id}`, form);
        toast.success('Request updated');
      } else {
        await apiPost('/admin/maintenance', form);
        toast.success('Request created');
      }
      setShowForm(false); load();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try { await apiDel(`/admin/maintenance/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const changeStatus = async (id, status) => {
    try { await apiPut(`/admin/maintenance/${id}`, { status }); load(); }
    catch { toast.error('Could not update status'); }
  };

  const cats = lookup.maintenance_categories || [];

  return (
    <div data-testid="admin-maintenance" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Administration</div>
          <h1 className="font-display text-2xl font-extrabold">Facility Maintenance</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track and resolve office / facility maintenance requests.</p>
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="maint-add-btn">
          <Plus size={15} /> New Request
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['', ...STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${statusFilter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#3b4872]'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Title</th><th>Category</th><th>Priority</th>
            <th>Location</th><th>Assigned To</th><th>Status</th>
            <th>Target</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">No maintenance requests</td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} data-testid={`maint-row-${r.id}`}>
                <td className="font-display font-semibold">{r.title}</td>
                <td className="text-xs">{r.category}</td>
                <td><span className={`tag text-[10px] ${PRI_COLOR[r.priority] || ''}`}>{r.priority}</span></td>
                <td className="text-xs">{r.location || '—'}</td>
                <td className="text-xs">{r.assigned_to || '—'}</td>
                <td>
                  <select value={r.status} onChange={e => changeStatus(r.id, e.target.value)}
                    className={`tag border cursor-pointer text-[10px] ${STA_COLOR[r.status] || ''}`}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                  </select>
                </td>
                <td className="text-xs">{r.target_date || '—'}</td>
                <td>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-[#3b4872]" data-testid={`maint-edit-${r.id}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-600" data-testid={`maint-del-${r.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Drawer */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
              <h2 className="font-display font-bold text-sm uppercase tracking-wider">{editing ? 'Edit Request' : 'New Maintenance Request'}</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </header>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="label">Title *</label>
                <input className="input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. AC not working in cabin 2" data-testid="maint-title" />
              </div>
              <div>
                <label className="label">Category *</label>
                <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="maint-category">
                  <option value="">Select category</option>
                  {cats.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Priority</label>
                  <select className="input" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Target Date</label>
                  <input type="date" className="input" value={form.target_date} onChange={e => setForm({...form, target_date: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="label">Location</label>
                <input className="input" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Ground Floor, Cabin 3" />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea className="input h-20 resize-none" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Reported By</label>
                  <input className="input" value={form.reported_by} onChange={e => setForm({...form, reported_by: e.target.value})} />
                </div>
                <div>
                  <label className="label">Assigned To</label>
                  <input className="input" value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} />
                </div>
              </div>
              {editing && (
                <div>
                  <label className="label">Resolution Notes</label>
                  <textarea className="input h-16 resize-none" value={form.resolution_notes || ''} onChange={e => setForm({...form, resolution_notes: e.target.value})} />
                </div>
              )}
            </form>
            <footer className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="maint-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
